import { Outlet, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Loader2, ExternalLink, Menu } from "lucide-react";
import { Sidebar } from "./Sidebar";
import { Logo } from "./Logo";
import { loadSession } from "@/lib/session";
import { getHome, syncNow, type HomePayload } from "@/lib/api";

/**
 * App shell. Renders the sidebar + outlet on every authenticated route.
 *
 * Owns the shared `HomePayload` (used by the Home cards and the sidebar) and
 * the one-time auto-sync: when a campground has never been synced, the shell
 * pulls its Roverpass data automatically so the owner never has to run a
 * manual setup step. A manual "Sync from Roverpass" lives in Knowledge for
 * when they change data in Roverpass later.
 */

import { createContext, useContext, useCallback } from "react";

interface ShellContextValue {
  home: HomePayload | null;
  loading: boolean;
  error: string | null;
  autoSyncing: boolean;
  refresh: () => Promise<void>;
}

const ShellContext = createContext<ShellContextValue | null>(null);

export function useShell() {
  const ctx = useContext(ShellContext);
  if (!ctx) throw new Error("useShell must be used inside <Shell>");
  return ctx;
}

export function Shell() {
  const session = loadSession();

  const [home, setHome] = useState<HomePayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [autoSyncing, setAutoSyncing] = useState(false);
  const autoSyncTried = useRef(false);

  // Mobile off-canvas sidebar. Close on every route change so tapping a nav
  // item (or any in-app navigation) dismisses the drawer.
  const [navOpen, setNavOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => { setNavOpen(false); }, [pathname]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getHome();
      setHome(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!session) return;
    void refresh();
    // Re-fetch on every focus so a quick alt-tab refresh shows current data.
    const onFocus = () => { void refresh(); };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // One-time automatic sync. `last_sync` is null only when the campground has
  // never run a sync job; a failed attempt leaves a (failed) row, so this
  // won't loop. The ref guards against the focus-refetch re-triggering it.
  useEffect(() => {
    if (!home || home.last_sync || autoSyncTried.current) return;
    autoSyncTried.current = true;
    (async () => {
      setAutoSyncing(true);
      try {
        await syncNow();
        await refresh();
      } catch {
        // Non-fatal: the dashboard still works (just empty). The owner can
        // retry from Knowledge → "Sync from Roverpass".
      } finally {
        setAutoSyncing(false);
      }
    })();
  }, [home, refresh]);

  if (!session) return <NoSession />;

  return (
    <ShellContext.Provider value={{ home, loading, error, autoSyncing, refresh }}>
      <div className="flex h-screen w-screen overflow-hidden bg-surface-2">
        <Sidebar
          campgroundName={home?.campground.name}
          gapsCount={home?.gaps.total}
          ticketsCount={home?.open_tickets}
          open={navOpen}
          onNavigate={() => setNavOpen(false)}
        />
        {/* Backdrop: only rendered while the mobile drawer is open, hidden on md+. */}
        {navOpen && (
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => setNavOpen(false)}
            className="fixed inset-0 z-40 bg-ink/40 md:hidden"
          />
        )}
        <main className="flex flex-1 flex-col overflow-y-auto scrollbar-thin">
          {/* Mobile top bar (< md): hamburger + logo, since the sidebar is off-canvas. */}
          <div className="flex items-center gap-3 border-b border-line bg-surface px-4 py-3 md:hidden">
            <button
              type="button"
              aria-label="Open menu"
              onClick={() => setNavOpen(true)}
              className="text-text-soft hover:text-text-strong"
            >
              <Menu size={20} strokeWidth={1.75} />
            </button>
            <Logo className="h-7 [&_svg]:h-full [&_svg]:w-auto" />
          </div>
          {autoSyncing && (
            <div className="flex items-center justify-center gap-2 bg-coral-soft px-4 py-2 text-sm text-coral-ink">
              <Loader2 size={14} className="animate-spin" />
              <span>Setting up your bot — importing your campground data from Roverpass…</span>
            </div>
          )}
          <div className="mx-auto w-full max-w-6xl px-4 py-6 md:px-8 md:py-8">
            <Outlet />
          </div>
        </main>
      </div>
    </ShellContext.Provider>
  );
}

/**
 * Shown when there's no session. The dashboard is entered exclusively through
 * the Roverpass handoff, so there's no manual sign-in — we point the owner
 * back to Roverpass.
 */
function NoSession() {
  return (
    <div className="grid h-screen w-screen place-items-center bg-ink px-4">
      <div className="w-full max-w-md text-center">
        <Logo className="mx-auto mb-6 h-9 [&_svg]:h-full [&_svg]:w-auto" />
        <div className="card bg-surface px-6 py-8">
          <div className="mx-auto mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-coral-soft text-coral">
            <ExternalLink size={18} strokeWidth={1.75} />
          </div>
          <p className="text-sm font-medium text-text-strong">Open from Roverpass</p>
          <p className="mt-2 text-sm text-text-soft">
            Your chatbot dashboard opens from your Roverpass account. Go to Roverpass,
            open your campground, and click <span className="font-medium text-text-strong">AI Chatbot</span>.
          </p>
        </div>
      </div>
    </div>
  );
}
